/**
 * The keyboard map (M1.17.1).
 *
 * **This is the feature H6 is about**: whether keyboard triage makes a large
 * feed list tractable. So it is a pure function from a key event to an action —
 * testable without a browser, and impossible to entangle with rendering.
 *
 * The bindings are the ones every reader since Google Reader has used, which
 * is not a lack of imagination: Maya has fifteen years of muscle memory and
 * inventing a better `j` would cost her more than it could possibly gain.
 *
 * ## Three rules that are easy to get wrong
 *
 * 1. **Never intercept a modifier.** `ctrl`/`cmd`/`alt` combinations belong to
 *    the browser and the operating system; binding `m` must not break
 *    `cmd+m`, and a reader that swallows `cmd+r` is a reader people close.
 * 2. **Never intercept while typing.** An `input`, a `textarea` or anything
 *    `contenteditable` owns the keyboard completely — otherwise typing a feed
 *    URL containing "s" stars an article.
 * 3. **`space` is scroll-*or*-advance.** It scrolls the open article while
 *    there is more of it, and only then moves on. Advancing immediately would
 *    make it impossible to read anything longer than the viewport.
 */

export type KeyAction =
  | 'next'
  | 'previous'
  | 'toggle-read'
  | 'star'
  | 'search'
  | 'mark-all-read'
  | 'open'
  | 'help'
  | 'close'
  | 'scroll-or-advance';

export interface KeyEventLike {
  readonly key: string;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly altKey?: boolean;
  readonly shiftKey?: boolean;
}

/** Whether the element currently focused owns the keyboard. */
export function isTypingTarget(target: unknown): boolean {
  if (typeof target !== 'object' || target === null) {
    return false;
  }

  const element = target as {
    tagName?: unknown;
    isContentEditable?: unknown;
  };

  const tag = typeof element.tagName === 'string' ? element.tagName.toUpperCase() : '';

  return (
    element.isContentEditable === true ||
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT'
  );
}

/**
 * The action for a key press, or `undefined` to leave the event alone.
 *
 * Returning `undefined` rather than throwing or defaulting matters: an
 * unhandled key must reach the browser untouched.
 */
export function actionFor(event: KeyEventLike, target?: unknown): KeyAction | undefined {
  if (event.ctrlKey === true || event.metaKey === true || event.altKey === true) {
    return undefined;
  }

  if (isTypingTarget(target)) {
    return undefined;
  }

  switch (event.key) {
    case 'j':
    case 'ArrowDown':
      return 'next';
    case 'k':
    case 'ArrowUp':
      return 'previous';
    case 'm':
      return 'toggle-read';
    case 's':
      return 'star';
    case 'A':
      // `shift+a`, which arrives as a capital. Two keys from `a` on purpose:
      // it is the only destructive action here and there is no undo.
      return event.shiftKey === true ? 'mark-all-read' : undefined;
    case 'Enter':
    case 'o':
      return 'open';
    case '?':
      return 'help';
    case '/':
      return 'search';
    case 'Escape':
      return 'close';
    case ' ':
      return 'scroll-or-advance';
    default:
      return undefined;
  }
}

/** What the help overlay lists (M1.17.1's fourth criterion). */
export const SHORTCUTS: readonly {
  readonly keys: string;
  readonly description: string;
}[] = [
  { keys: 'j / ↓', description: 'Next item' },
  { keys: 'k / ↑', description: 'Previous item' },
  { keys: 'space', description: 'Scroll the article, then move on' },
  { keys: 'm', description: 'Toggle read' },
  { keys: 's', description: 'Star' },
  { keys: 'enter / o', description: 'Open the original in a new tab' },
  { keys: 'shift + a', description: 'Mark everything in view as read' },
  { keys: '/', description: 'Search' },
  { keys: '?', description: 'This list' },
  { keys: 'esc', description: 'Close' },
];

export interface ScrollTarget {
  readonly scrollTop: number;
  readonly scrollHeight: number;
  readonly clientHeight: number;
}

/**
 * Whether `space` should scroll the article or advance to the next item.
 *
 * The tolerance is what makes this feel right rather than technically correct:
 * within a few pixels of the bottom, a reader has finished, and asking them to
 * press space twice because of sub-pixel rounding is the sort of detail that
 * makes an application feel grudging.
 */
export function shouldAdvance(target: ScrollTarget, tolerance = 8): boolean {
  return target.scrollTop + target.clientHeight >= target.scrollHeight - tolerance;
}
